import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import {
  setRoyalCollectOutroTransitionActive,
  setRoyalCollectActive,
  resetRoyalCollectState,
  setCriticalAnimationPlaying,
  clearFeatureStateToBase,
  setFeatureStateFromFreeRounds,
  refreshGameData,
  syncBalance,
  setSpinComplete,
  setBalanceAlreadyUpdated,
  resumeAutoPlay,
} from "../../../../State/actions";
import {
  selectRoyalCollectOutroTransitionActive,
  selectFreeRoundsStatus,
  selectFreeRoundsCompletionPending,
} from "../../../../State/selectors";
import store from "../../../../State/Store/store";

import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { useReelRefs } from "../../../../Contexts/ReelRefsContext";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";
import { Settings } from "../../../../settings";
import anime from "animejs";
import * as PIXI from "pixi.js";

const isPrimaryTheme = Settings.APP.UI_THEME === "primary";

/**
 * RoyalCollectSceneTransition
 *
 * Gradient overlay transition for exiting the Royal Collect feature.
 * Uses the same gradient pattern as IntroTransitionOverlay / ErrorRecoveryOverlay.
 *
 * RC overlays on the base game — the base scene is already ready underneath,
 * we just need to switch the background spine from feature_idle → base_idle
 * and clear feature state while the screen is covered.
 *
 * Flow:
 *   1. Outro dialog finishes → dispatches royalCollectTransitionActive(true)
 *   2. Gradient fades in (covers screen)
 *   3. While covered: deactivate RC, clear feature state, switch background to base_idle
 *   4. Gradient fades out to reveal the base game
 *   5. On complete: sync balance, fade in music, unblock input
 */
const RoyalCollectSceneTransition = () => {
  const dispatch = useDispatch();

  const { dimensions } = useDimensions();
  const { backgroundAnimationControllerRef } = useReelRefs();
  const overlayRef = useRef(null);
  const animationRef = useRef(null);
  const royalCollectOutroTransitionActive = useSelector(selectRoyalCollectOutroTransitionActive);
  const freeRoundsStatus = useSelector(selectFreeRoundsStatus);
  const freeRoundsCompletionPending = useSelector(selectFreeRoundsCompletionPending);

  // Memoize container zIndex — above everything
  const containerZIndex = useMemo(() => 10000, []);

  /**
   * Create overlay sprite — theme-aware:
   * Primary: diagonal gradient matching the primary loader
   * Secondary: solid black matching the secondary loader
   */
  const createOverlaySprite = useCallback(() => {
    const canvas = document.createElement("canvas");
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    const ctx = canvas.getContext("2d");

    if (isPrimaryTheme) {
      // Diagonal linear gradient (top-left → bottom-right) — matches primary loader
      const linear = ctx.createLinearGradient(0, 0, dimensions.width, dimensions.height);
      linear.addColorStop(0, "#2a1f5e");
      linear.addColorStop(0.4, "#150e3a");
      linear.addColorStop(1, "#050020");
      ctx.fillStyle = linear;
    } else {
      // Solid black — matches secondary loader
      ctx.fillStyle = "#000000";
    }
    ctx.fillRect(0, 0, dimensions.width, dimensions.height);

    const texture = PIXI.Texture.from(canvas);
    const sprite = new PIXI.Sprite(texture);
    sprite.width = dimensions.width;
    sprite.height = dimensions.height;
    return sprite;
  }, [dimensions.width, dimensions.height]);

  // Handle transition animation
  useEffect(() => {
    if (royalCollectOutroTransitionActive && overlayRef.current) {
      // Clear any existing animation
      if (animationRef.current) {
        animationRef.current.pause();
      }

      // Remove old overlay and create new one
      overlayRef.current.removeChildren();
      overlayRef.current.addChild(createOverlaySprite());

      // Create transition: fade in → hold & clear feature → fade out
      const animation = anime.timeline({
        complete: () => {
          dispatch(setRoyalCollectOutroTransitionActive(false));

          // Resume autoplay if it was paused for this feature
          const pausedSpins = store.getState().settings.autoplayPausedSpins;
          if (pausedSpins !== null) {
            dispatch(resumeAutoPlay());
          }
        },
      });

      // Phase 1: Fade in gradient to cover the screen
      animation.add({
        targets: overlayRef.current,
        alpha: [0, 1],
        duration: 300,
        easing: "easeInQuad",
        complete: () => {
          // Screen is now entirely covered — safe to clear RC

          // Fully reset all Royal Collect state (triggered, reels, round, respins, readiness)
          dispatch(setRoyalCollectActive(false));
          dispatch(resetRoyalCollectState());

          // Clear feature state — restore base game state
          if (freeRoundsStatus === "active") {
            const fr = store.getState().freeRounds;
            dispatch(setFeatureStateFromFreeRounds({
              betValue: fr.betValue,
              betLevel: fr.betLevel,
              count: fr.count,
              performed: fr.performed,
              totalWin: fr.totalWin,
            }));
          } else {
            dispatch(clearFeatureStateToBase());
          }

          // Switch background from feature_idle → base_idle
          const bgController = backgroundAnimationControllerRef.current;
          if (bgController?.backgroundSpine) {
            if (bgController.backgroundSpine.state) {
              bgController.backgroundSpine.state.clearTracks();
            }
            bgController.backgroundSpine.skeleton.setBonesToSetupPose();
            bgController.playBaseIdle?.();
          }
        },
      });

      // Phase 2: Hold overlay while React re-renders the base game
      animation.add({
        targets: overlayRef.current,
        alpha: 1,
        duration: 300,
      });

      // Phase 3: Fade out gradient to reveal the base game
      animation.add({
        targets: overlayRef.current,
        alpha: [1, 0],
        duration: 300,
        easing: "easeOutQuad",
        complete: () => {
          // Sync balance and mark spin complete
          dispatch(syncBalance());
          dispatch(setSpinComplete(true));

          // If free rounds completion is pending, prevent double balance update
          if (freeRoundsCompletionPending) {
            dispatch(setBalanceAlreadyUpdated(true));
          }

          // Sync RGS state so next spin works without waiting for heartbeat
          dispatch(refreshGameData());

          // Fade in base game music
          soundManager.currentBackgroundMusic = SOUND_NAME.background_music;
          soundManager.fadeIn(SOUND_NAME.background_music, 500);

          // Unblock input
          dispatch(setCriticalAnimationPlaying(false));
        },
      });

      animationRef.current = animation;

      return () => {
        if (animationRef.current) {
          animationRef.current.pause();
          animationRef.current = null;
        }
      };
    }
  }, [
    royalCollectOutroTransitionActive,
    createOverlaySprite,
    dispatch,
    backgroundAnimationControllerRef,
    freeRoundsStatus,
    freeRoundsCompletionPending,
  ]);

  // Only render when there's an active transition
  if (!royalCollectOutroTransitionActive) return null;

  return (
    <Container
      name="royal_collect_scene_transition"
      ref={overlayRef}
      x={0}
      y={0}
      zIndex={containerZIndex}
    />
  );
};

export default RoyalCollectSceneTransition;
